import { useCallback } from 'react';

import {
  ExtraSectionIdentifiers,
  useActivityBuilderApollo,
} from '../activity-builder.apollo';
import type { InteractiveContentStyleBackgroundFragment } from './styles.graphql.generated';
import { UPDATE_LESSON_ACTIVITIES_DEFAULT_BACKGROUND_STYLE } from './styles.graphql';
import type {
  UpdateLessonActivitiesDefaultBackgroundStyleMutation,
  UpdateLessonActivitiesDefaultBackgroundStyleMutationVariables,
} from './styles.graphql.generated';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useActivityBuilderApolloContext } from '../activity-builder.apollo.context';
import { useGetInteractiveLessonParams } from '../activity-builder.hooks';
import type { ImageInput } from '../../../gql/schema-types';
import { BackgroundImageUrls } from 'approot/shared/layout/layout.constants';
import { InquisitiveDraftAction } from 'global/graphql/scalars.type';
import {
  modifyActivitiesPublishChanges,
  resetInteractiveContentInCache,
} from '../activity-builder.apollo.utils';
import { BACKGROUND_COLOURS } from './styles-drawer.constants';
import { ResponsiveImage } from 'approot/shared/images/imgix-responsive.constants';
import { useActivityBuilderRequest } from '../activity-builder-request.apollo';
import customNanoId from 'lib/nanoid/nanoid';
import { useGetDraftActivitiesFromCache } from '../logic/hooks/use-get-draft-activities-from-cache';
import type { Pluck } from 'lib/gql/pluck';

export function getBackgroundImageUrls(images?: ResponsiveImage[] | null) {
  return images
    ? {
        base: images[1]?.urls.at(-1)?.url || '',
        l: images[2]?.urls.at(-1)?.url || '',
        xl: images[3]?.urls.at(-1)?.url || '',
        xxl: images[4]?.urls.at(-1)?.url || '',
        xxxl: images[5]?.urls.at(-1)?.url || '',
      }
    : undefined;
}

export function getBackgroundColour(backgroundColour?: string | null) {
  return BACKGROUND_COLOURS.find(
    c => c.base === backgroundColour || c.colour1 === backgroundColour
  )?.colour1;
}

export const useSlideBackground = (
  defaultBackgroundStyle?: Pluck<
    InteractiveContentStyleBackgroundFragment,
    'defaultBackgroundStyle'
  > | null,
  coverResponsiveImage?: ResponsiveImage[] | null
) => {
  const {
    data: { activeSectionId },
  } = useActivityBuilderApollo();

  let backgroundColour;
  let backgroundImage: BackgroundImageUrls | undefined;

  if (activeSectionId === ExtraSectionIdentifiers.Introduction) {
    backgroundImage = getBackgroundImageUrls(coverResponsiveImage);
  } else if (defaultBackgroundStyle) {
    backgroundColour = getBackgroundColour(
      defaultBackgroundStyle.backgroundColour
    );
    backgroundImage = getBackgroundImageUrls(
      defaultBackgroundStyle.backgroundImage?.responsive
    );
  }

  return {
    backgroundColour: backgroundColour || undefined,
    backgroundImage: backgroundImage || undefined,
  };
};

export const useUpdateDefaultBackground = () => {
  const { showError } = useSnackbar();

  const { createRequest, clearRequest } = useActivityBuilderRequest();
  const { interactiveLessonClone, setInteractiveLessonClone } =
    useActivityBuilderApolloContext();

  const { contentId, contentType, userClassId } =
    useGetInteractiveLessonParams();
  const data = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });

  const [
    updateDefaultBackgroundColour,
    { client, loading: loadingUpdateDefaultBackgroundColour },
  ] = useLoggingMutation<
    UpdateLessonActivitiesDefaultBackgroundStyleMutation,
    UpdateLessonActivitiesDefaultBackgroundStyleMutationVariables
  >(UPDATE_LESSON_ACTIVITIES_DEFAULT_BACKGROUND_STYLE, {
    onCompleted: () => {
      if (interactiveLessonClone) {
        setInteractiveLessonClone(null);
      }
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      resetInteractiveContentInCache({
        client,
        contentType,
        classId: userClassId,
        interactiveContent: interactiveLessonClone,
      });
      showError('There was an error updating all background styles');
    },
    fetchPolicy: 'no-cache',
  });

  const [
    updateDefaultBackgroundImage,
    { loading: loadingUpdateDefaultBackgroundImage },
  ] = useLoggingMutation<
    UpdateLessonActivitiesDefaultBackgroundStyleMutation,
    UpdateLessonActivitiesDefaultBackgroundStyleMutationVariables
  >(UPDATE_LESSON_ACTIVITIES_DEFAULT_BACKGROUND_STYLE, {
    onCompleted: () => {
      modifyActivitiesPublishChanges({
        client,
        id: contentId,
        contentType,
      });
    },
    onError: () => {
      showError('There was an error updating all background styles');
    },
  });

  const handleUpdateDefaultBackgroundColour = useCallback(
    (colour: string, draftAction?: InquisitiveDraftAction) => {
      if (!data) return;

      const requestId = createRequest(customNanoId());

      data.styles &&
        resetInteractiveContentInCache({
          client,
          contentType,
          classId: userClassId,
          interactiveContent: {
            ...data,
            styles: {
              ...data.styles,
              defaultBackgroundStyle: {
                __typename: 'DefaultBackgroundStyle',
                backgroundColour: colour,
                backgroundImage: null,
              },
            },
          },
        });

      updateDefaultBackgroundColour({
        variables: {
          input: {
            contentId,
            backgroundColour: colour,
            backgroundImageInput: null,
            draftAction,
          },
        },
      }).finally(() => {
        clearRequest(requestId);
      });
    },
    [
      updateDefaultBackgroundColour,
      client,
      data,
      contentId,
      contentType,
      createRequest,
      clearRequest,
      userClassId,
    ]
  );

  const handleUpdateDefaultBackgroundImage = useCallback(
    (image: ImageInput) => {
      const requestId = createRequest(customNanoId());
      updateDefaultBackgroundImage({
        variables: {
          input: {
            contentId,
            backgroundColour: null,
            backgroundImageInput: image,
          },
        },
      }).finally(() => {
        clearRequest(requestId);
      });
    },
    [updateDefaultBackgroundImage, contentId, createRequest, clearRequest]
  );

  return {
    handleUpdateDefaultBackgroundColour,
    handleUpdateDefaultBackgroundImage,
    loadingUpdateDefaultBackgroundColour,
    loadingUpdateDefaultBackgroundImage,
  };
};
